import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Warehouse,
  Store,
  Users,
  Package,
  TrendingUp,
  ShoppingCart,
  AlertTriangle,
  CheckCircle,
  Bell,
  Truck,
  FileText,
  ArrowRight,
  ClipboardList,
  Clock,
  AlarmClock,
  PackageCheck,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { warehousesAPI, storesAPI, usersAPI, brandsAPI, categoriesAPI, uomAPI, taxAPI, stockRequestsAPI , productsAPI, securityAPI, ledgersAPI, offersAPI } from '@/services/api';
import api from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';
import { OnlineOrdersWidget } from '@/components/hq/ecom/OnlineOrdersWidget';
import { ExpiryAlertCard } from '@/components/shared/ExpiryAlertCard';

const StatCard = ({ title, value, icon: Icon, color, loading }) => (
  <Card className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-800">
            {loading ? '...' : value}
          </p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AlertCard = ({ title, count, icon: Icon, color, onClick, description }) => (
  <Card 
    className={`bg-white border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-all ${count > 0 ? `border-l-4 border-l-${color}-500` : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${count > 0 ? `bg-${color}-100` : 'bg-gray-100'} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${count > 0 ? `text-${color}-600` : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="font-medium text-gray-800">{title}</p>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`text-2xl font-bold ${count > 0 ? `text-${color}-600` : 'text-gray-400'}`}>{count}</p>
          {count > 0 && (
            <Badge className={`bg-${color}-100 text-${color}-700 text-[10px]`}>
              <Bell className="w-3 h-3 mr-1" /> Action needed
            </Badge>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const HQDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    warehouses: 0,
    stores: 0,
    users: 0,
    brands: 0,
    categories: 0,
    uom: 0,
    taxGroups: 0,
    products: 0
  });
  const [alerts, setAlerts] = useState({
    pending_franchise_po_requests: 0,
    pending_stock_transfer_requests: 0,
    pending_transfer_orders: 0,
    total_pending_requests: 0
  });
  const [stockRequestAlerts, setStockRequestAlerts] = useState({
    pending_stock_requests: 0,
    urgent_requests: 0,
    recent_requests: [],
    warehouse_summary: {}
  });
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [marginAlerts, setMarginAlerts] = useState({ count: 0, recent: [] });
  const [paymentDue, setPaymentDue] = useState({ summary: {}, bills: [] });
  const [offerAlerts, setOfferAlerts] = useState({ expiring_soon: [], recently_expired: [], expiring_count: 0, expired_count: 0 });
  const [newStoreRequests, setNewStoreRequests] = useState({ count: 0, items: [] });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [warehouses, stores, users, brands, categories, uom, tax, products] = await Promise.all([
          warehousesAPI.list().catch(() => ({ data: [] })),
          storesAPI.list().catch(() => ({ data: [] })),
          usersAPI.list({ page: 1, page_size: 1 }).catch(() => ({ data: { total: 0 } })),
          brandsAPI.list().catch(() => ({ data: [] })),
          categoriesAPI.list().catch(() => ({ data: [] })),
          uomAPI.list().catch(() => ({ data: [] })),
          taxAPI.list().catch(() => ({ data: [] })),
          productsAPI.list({ page: 1, page_size: 1, is_active: true }).catch(() => ({ data: { total: 0 } }))
        ]);

        setStats({
          warehouses: warehouses.data?.length || 0,
          stores: stores.data?.length || 0,
          users: users.data?.total || 0,
          brands: brands.data?.length || 0,
          categories: categories.data?.length || 0,
          uom: uom.data?.length || 0,
          taxGroups: tax.data?.length || 0,
          products: products.data?.total || 0
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAlerts = async () => {
      try {
        const response = await api.get('/transfers/hq/dashboard/alerts');
        setAlerts(response.data);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      } finally {
        setAlertsLoading(false);
      }
    };

    const fetchStockRequestAlerts = async () => {
      try {
        const response = await api.get('/stock-requests/hq/dashboard/alerts');
        setStockRequestAlerts(response.data);
      } catch (error) {
        console.error('Failed to fetch stock request alerts:', error);
      }
    };

    const fetchMarginAlerts = async () => {
      // Super Admin only endpoint - skip it for other HQ users to avoid noisy 403s
      const roleName = user?.role_name || user?.role_code || '';
      if (roleName && !roleName.toLowerCase().includes('super admin')) return;
      try {
        const response = await securityAPI.marginAlerts(5);
        setMarginAlerts(response.data);
      } catch (error) {
        console.error('Failed to fetch margin alerts:', error);
      }
    };

    fetchStats();
    fetchAlerts();
    fetchStockRequestAlerts();
    fetchMarginAlerts();
    (async () => {
      try {
        const res = await ledgersAPI.vendorPaymentReminders({ days_ahead: 7 });
        setPaymentDue(res.data);
      } catch (error) {
        console.error('Failed to fetch vendor payment reminders:', error);
      }
    })();
    (async () => {
      try {
        const res = await offersAPI.getAlerts();
        setOfferAlerts(res.data || {});
      } catch (error) {
        console.error('Failed to fetch offer alerts:', error);
      }
    })();
    (async () => {
      try {
        const res = await api.get('/transfers/hq/alerts/new-store-requests', { params: { limit: 20 } });
        setNewStoreRequests(res.data || { count: 0, items: [] });
      } catch (error) {
        console.error('Failed to fetch new store stock requests:', error);
      }
    })();
  }, []);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="hq-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Welcome back, {user?.full_name}!
        </h1>
        <p className="text-gray-500">
          Here&apos;s what&apos;s happening with your SY-Store network today.
        </p>
      </div>

      <ExpiryAlertCard scope="hq" reportPath="/hq/reports/expiry" />
      <OnlineOrdersWidget />

      {/* Margin reduction alert - management attention */}
      {marginAlerts.count > 0 && (
        <div
          className="mb-8 rounded-xl border-2 border-red-300 bg-red-600 text-white p-5 shadow-lg cursor-pointer hover:bg-red-700 transition-colors"
          onClick={() => navigate('/hq/margin-log')}
          data-testid="margin-reduction-alert"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-base">
                  Management attention: {marginAlerts.count} margin reduction{marginAlerts.count > 1 ? 's' : ''} pending review
                </p>
                <ul className="mt-2 space-y-1 text-sm text-red-50">
                  {marginAlerts.recent.map(a => (
                    <li key={a.id} data-testid={`margin-alert-item-${a.id}`}>
                      {a.product_name} ({a.sku}) · {Number(a.old_margin_percent).toFixed(2)}% →{' '}
                      {Number(a.new_margin_percent).toFixed(2)}% · {a.grn_number || 'Product Master'}
                      {a.vendor_name ? ` · ${a.vendor_name}` : ''} · by {a.changed_by_name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Badge className="bg-white text-red-700 text-base px-3 py-1" data-testid="margin-alert-count">
              {marginAlerts.count}
            </Badge>
          </div>
          <p className="text-xs text-red-100 mt-3">Click to open the full Margin Change Log</p>
        </div>
      )}

      {/* Vendor payment due reminders */}
      {(paymentDue.summary?.overdue_count > 0 || paymentDue.summary?.due_soon_count > 0) && (
        <div
          className={`mb-8 rounded-xl border-2 p-5 cursor-pointer transition-colors ${
            paymentDue.summary?.overdue_count > 0
              ? 'border-red-300 bg-red-50 hover:bg-red-100'
              : 'border-amber-300 bg-amber-50 hover:bg-amber-100'
          }`}
          onClick={() => navigate('/hq/ledgers/vendor')}
          data-testid="vendor-payment-due-alert"
        >
          <div className="flex items-start gap-3">
            <AlarmClock className={`w-6 h-6 mt-0.5 shrink-0 ${paymentDue.summary?.overdue_count > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`font-semibold ${paymentDue.summary?.overdue_count > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                Vendor payments: {paymentDue.summary?.overdue_count || 0} bill(s) overdue
                ₹{Math.round(Number(paymentDue.summary?.overdue_amount || 0)).toLocaleString('en-IN')}
                {' · '}{paymentDue.summary?.due_soon_count || 0} due in the next 7 days
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-700">
                {(paymentDue.bills || []).slice(0, 4).map(b => (
                  <li key={b.ledger_entry_id} data-testid={`payment-due-item-${b.ledger_entry_id}`}>
                    {b.vendor_name} · {b.grn_number || b.invoice_number || 'Bill'} ·
                    ₹{Math.round(Number(b.pending_amount || 0)).toLocaleString('en-IN')} ·
                    {b.status === 'overdue'
                      ? ` ${b.days_overdue} day(s) late (due ${b.due_date})`
                      : b.status === 'due_today' ? ' due today' : ` due in ${b.days_to_due} day(s)`}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-2">Click to open the Payment Due panel in Vendor Ledger</p>
            </div>
            <Badge className={paymentDue.summary?.overdue_count > 0 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}
              data-testid="payment-due-count">
              {(paymentDue.summary?.overdue_count || 0) + (paymentDue.summary?.due_soon_count || 0)}
            </Badge>
          </div>
        </div>
      )}

      {/* Offer Alerts: expiring soon or recently expired */}
      {(offerAlerts.expiring_count > 0 || offerAlerts.expired_count > 0) && (
        <div
          onClick={() => navigate('/hq/offers')}
          className={`mb-6 rounded-xl border-l-4 shadow-sm p-4 cursor-pointer transition-colors ${
            offerAlerts.expired_count > 0 ? 'border-red-500 bg-red-50 hover:bg-red-100' : 'border-amber-500 bg-amber-50 hover:bg-amber-100'
          }`}
          data-testid="offer-alerts-card"
        >
          <div className="flex items-start gap-3">
            <Tag className={`w-6 h-6 mt-0.5 shrink-0 ${offerAlerts.expired_count > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`font-semibold ${offerAlerts.expired_count > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                {offerAlerts.expired_count > 0 && `${offerAlerts.expired_count} offer${offerAlerts.expired_count > 1 ? 's' : ''} expired recently`}
                {offerAlerts.expired_count > 0 && offerAlerts.expiring_count > 0 && ' · '}
                {offerAlerts.expiring_count > 0 && `${offerAlerts.expiring_count} expiring in next 3 days`}
              </p>
              <ul className={`mt-1 text-sm space-y-0.5 ${offerAlerts.expired_count > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                {[...(offerAlerts.recently_expired || []).slice(0, 3).map(o => ({ ...o, state: 'expired' })),
                  ...(offerAlerts.expiring_soon || []).slice(0, 3).map(o => ({ ...o, state: 'expiring' }))].map(o => (
                  <li key={o.id}>
                    <span className="font-medium">{o.name}</span>
                    <span className="opacity-70"> ({o.code})</span>
                    {' · '}
                    <span className="text-xs opacity-80">
                      {o.state === 'expired' ? 'expired' : 'expires'} {new Date(o.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs opacity-75 mt-2">Click to open the Offers page</p>
            </div>
            <Badge className={offerAlerts.expired_count > 0 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}
              data-testid="offer-alerts-count">
              {offerAlerts.expired_count + offerAlerts.expiring_count}
            </Badge>
          </div>
        </div>
      )}

      {/* New Store/Franchise Stock Requests */}
      {newStoreRequests.count > 0 && (() => {
        const overdueItems = (newStoreRequests.items || []).filter(r => {
          const ageHours = (Date.now() - new Date(r.created_at).getTime()) / 3600000;
          return ageHours >= 24;
        });
        const hasOverdue = overdueItems.length > 0;
        return (
        <div
          onClick={() => navigate('/hq/inventory/stock-requests')}
          className={`mb-6 rounded-xl border-l-4 shadow-sm p-4 cursor-pointer transition-colors ${hasOverdue ? 'border-red-500 bg-red-50 hover:bg-red-100' : 'border-sky-500 bg-sky-50 hover:bg-sky-100'}`}
          data-testid="new-store-requests-card"
        >
          <div className="flex items-start gap-3">
            <PackageCheck className={`w-6 h-6 mt-0.5 shrink-0 ${hasOverdue ? 'text-red-600' : 'text-sky-600'}`} />
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${hasOverdue ? 'text-red-800' : 'text-sky-800'}`}>
                {newStoreRequests.count} new stock request{newStoreRequests.count > 1 ? 's' : ''} from store{newStoreRequests.count > 1 ? 's' : ''}/franchise{newStoreRequests.count > 1 ? 's' : ''}
                {hasOverdue && (
                  <span className="ml-2 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full font-bold">
                    {overdueItems.length} overdue &gt;24h
                  </span>
                )}
              </p>
              <ul className="mt-1 text-sm space-y-0.5 text-sky-700">
                {newStoreRequests.items.slice(0, 5).map(r => {
                  const createdMs = new Date(r.created_at).getTime();
                  const ageMinutes = Math.max(0, Math.floor((Date.now() - createdMs) / 60000));
                  const ageHours = Math.floor(ageMinutes / 60);
                  const overdue = ageHours >= 24;
                  const ageLabel = ageHours >= 24
                    ? `${Math.floor(ageHours / 24)}d ${ageHours % 24}h ago`
                    : ageHours >= 1
                    ? `${ageHours}h ${ageMinutes % 60}m ago`
                    : `${ageMinutes}m ago`;
                  return (
                  <li key={r.id} className="truncate">
                    New stock request from <span className="font-semibold">{r.requester_name}</span>
                    <span className="opacity-70"> ({r.requester_code})</span>
                    {r.ownership === 'franchise' && (
                      <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Franchise</span>
                    )}
                    {r.priority && r.priority !== 'normal' && (
                      <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${r.priority === 'urgent' ? 'bg-red-100 text-red-700' : r.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.priority}
                      </span>
                    )}
                    <span className={`ml-1 text-xs px-1.5 py-0.5 rounded font-medium ${overdue ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-sky-100 text-sky-700'}`}>
                      {overdue ? 'overdue · ' : ''}{ageLabel}
                    </span>
                    <span className="opacity-70"> · {r.total_items} item{r.total_items !== 1 ? 's' : ''}</span>
                  </li>
                  );
                })}
                {newStoreRequests.count > 5 && (
                  <li className="text-xs opacity-75">… and {newStoreRequests.count - 5} more</li>
                )}
              </ul>
              <p className="text-xs opacity-75 mt-2">Click to open Stock Requests</p>
            </div>
            <Badge className={hasOverdue ? 'bg-red-600 text-white' : 'bg-sky-600 text-white'} data-testid="new-store-requests-count">
              {newStoreRequests.count}
            </Badge>
          </div>
        </div>
        );
      })()}

      {/* Alerts Section */}
      {(alerts.pending_franchise_po_requests > 0 || alerts.pending_stock_transfer_requests > 0 || stockRequestAlerts.pending_stock_requests > 0) && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-800">Action Required</h2>
            <Badge className="bg-amber-100 text-amber-700">
              {alerts.total_pending_requests + stockRequestAlerts.pending_stock_requests} pending
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AlertCard
              title="Warehouse Stock Requests"
              count={stockRequestAlerts.pending_stock_requests}
              icon={ClipboardList}
              color="teal"
              description={stockRequestAlerts.urgent_requests > 0 ? `${stockRequestAlerts.urgent_requests} urgent/high priority` : "Stock requests from warehouses"}
              onClick={() => navigate('/hq/inventory/stock-requests')}
            />
            <AlertCard
              title="Franchise PO Requests"
              count={alerts.pending_franchise_po_requests}
              icon={FileText}
              color="purple"
              description="Stock requests from franchise stores"
              onClick={() => navigate('/hq/inventory/stock-requests')}
            />
            <AlertCard
              title="Stock Transfer Requests"
              count={alerts.pending_stock_transfer_requests}
              icon={Truck}
              color="blue"
              description="Transfer requests from owned stores"
              onClick={() => navigate('/hq/inventory/stock-requests')}
            />
          </div>
        </div>
      )}

      {/* Stock Request Alert Banner - Only show if pending requests exist */}
      {stockRequestAlerts.pending_stock_requests > 0 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <ClipboardList className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold">
                {stockRequestAlerts.pending_stock_requests} Pending Stock Request{stockRequestAlerts.pending_stock_requests !== 1 ? 's' : ''} from Warehouses
              </p>
              <p className="text-sm text-white/80">
                {Object.keys(stockRequestAlerts.warehouse_summary).length > 0 && 
                  Object.entries(stockRequestAlerts.warehouse_summary).slice(0, 3).map(([wh, data]) => 
                    `${wh}: ${data.count}`
                  ).join(' | ')
                }
                {stockRequestAlerts.urgent_requests > 0 && ` • ${stockRequestAlerts.urgent_requests} urgent`}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/hq/inventory/stock-requests')}
            className="bg-white text-teal-600 hover:bg-white/90"
            data-testid="view-stock-requests-btn"
          >
            Review Requests
          </Button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Warehouses"
          value={stats.warehouses}
          icon={Warehouse}
          color="blue"
          loading={loading}
        />
        <StatCard
          title="Stores"
          value={stats.stores}
          icon={Store}
          color="emerald"
          loading={loading}
        />
        <StatCard
          title="Users"
          value={stats.users}
          icon={Users}
          color="purple"
          loading={loading}
        />
        <StatCard
          title="Products"
          value={stats.products}
          icon={Package}
          color="orange"
          loading={loading}
        />
      </div>

      {/* Master Data Status */}
      <Card className="bg-white border-gray-200 shadow-sm mb-8">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Master Data Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Brands</span>
              </div>
              <span className="font-semibold text-gray-800">{stats.brands}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Categories</span>
              </div>
              <span className="font-semibold text-gray-800">{stats.categories}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Units of Measurement</span>
              </div>
              <span className="font-semibold text-gray-800">{stats.uom}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-700">Tax Groups (GST)</span>
              </div>
              <span className="font-semibold text-gray-800">{stats.taxGroups}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-orange-500" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => navigate('/hq/warehouses')}
            >
              <Warehouse className="w-6 h-6 text-blue-600" />
              <span>Add Warehouse</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-emerald-50 hover:border-emerald-300"
              onClick={() => navigate('/hq/stores')}
            >
              <Store className="w-6 h-6 text-emerald-600" />
              <span>Add Store</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => navigate('/hq/users')}
            >
              <Users className="w-6 h-6 text-purple-600" />
              <span>Add User</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-orange-50 hover:border-orange-300"
              onClick={() => navigate('/hq/products')}
            >
              <Package className="w-6 h-6 text-orange-600" />
              <span>Add Product</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card className="bg-white border-gray-200 shadow-sm mt-8">
        <CardHeader>
          <CardTitle className="text-gray-800">System Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-xs">Your Role</p>
              <p className="font-semibold text-gray-800">{user?.role_name || user?.role || 'HQ User'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-xs">Entity Type</p>
              <p className="font-semibold text-gray-800 capitalize">{user?.entity_type || 'Hq'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-xs">Last Login</p>
              <p className="font-semibold text-gray-800">{formatDate(new Date())}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HQDashboard;
